import { Project } from 'core/models/project';
import { Vehicle } from 'core/models/vehicle';
import { Schedule } from 'core/models/FMS/Schedule';
import { Telemetry } from 'core/models/FMS/Telemetry';
import { StableWebSocket } from 'infrastructure/utility/StableWebSocket';
import { StreamConnector } from '../StreamConnector';
import { TokenKeeper } from '../TokenKeeper';

// client implementation for FMS WebSocket API
// @see https://github.com/tier4/AutowareFMSAPI/blob/develop/docs/src/spec/internal.yaml

export class SequenceError extends Error {
}

export class FMSWebsocketAPIError extends Error {
}

export class UnknownMessageError extends FMSWebsocketAPIError {
    constructor(public readonly msg: Record<string, unknown>) {
        super('unknown message');
    }
}

export class UnsupportedMessageError extends FMSWebsocketAPIError {
    constructor(public readonly event: MessageEvent) {
        super('unsupported message');
    }
}

export type VehicleTelemetry = {
    project_id: Project.Id;
    vehicle_id: Vehicle.Id;
    // additional fields may follow
} & Telemetry;

function mightBeVehicleTelemetry(json: Record<string, unknown>): json is VehicleTelemetry {
    return 'project_id' in json && typeof json.project_id === 'string'
        && 'vehicle_id' in json && typeof json.vehicle_id === 'string'
        && 'updated_at' in json && typeof json.updated_at === 'string';
}

function isSchedule(json: Record<string, unknown>): json is Schedule {
    return 'schedule_id' in json;
}

export type VehicleMetadataUpdates = {
    updates_type: 'CREATED' | 'MODIFIED' | 'DELETED';
    // additional fields may follow
}

function isVehicleMetadataUpdates(json: Record<string, unknown>): json is VehicleMetadataUpdates {
    return 'updates_type' in json;
}

type VehicleTelemetryRequest = {
    token: string;
    channel: 'vehicleTelemetry',
    operation: 'subscribe' | 'unsubscribe',
    resource_id: Vehicle.Id,
};

type FMSWebsocketRequest = VehicleTelemetryRequest;

export class FMSWebsocketAPI {
    private ws?: StableWebSocket;
    private reconnecting = false;

    constructor(private readonly connector: StreamConnector, private readonly tokenKeeper: TokenKeeper) {
    }

    async connect(projectId: Project.Id): Promise<void> {
        if (this.ws) {
            throw new SequenceError('not yet disconnected');
        }

        const { token } = await this.tokenKeeper.get();
        const ws = new StableWebSocket(() => this.connector(`/v1/ws?project_id=${projectId}&token=${token}`));
        ws.onerror = e => console.error(e);
        ws.onclose = () => {
            console.warn('ws closed');
            this.waitForReconnect();
        };
        ws.onmessage = msg => {
            const json = this.parseMessage(msg);
            if (!json) this.onUnsupportedMessage(msg);
            else if (isSchedule(json)) {
                this.onSchedule(json);
            }
            else if (isVehicleMetadataUpdates(json)) {
                this.onVehicleMetadataUpdates(json);
            }
            else if (mightBeVehicleTelemetry(json)) {
                this.onVehicleTelemetry(json);
            }
            else {
                this.onUnknownMessage(json);
            }
        };

        this.ws = await ws.connect();
    }

    async disconnect() {
        await this.ws?.disconnect();
        this.ws = undefined;
        this.reconnecting = false;
    }

    private readonly listeners = {
        vehicleTelemetry: new Set<(telemetryVehicleTelemetry: VehicleTelemetry) => void>(),
        schedule: new Set<(schedule: Schedule) => void>(),
        vehicleMetadataUpdates: new Set<(updates: VehicleMetadataUpdates) => void>(),
        reconnect: new Set<() => void>(),
        error: new Set<(reason: FMSWebsocketAPIError) => void>(),
    };

    addEventListener(event: 'vehicleTelemetry', cb: (telemetryVehicleTelemetry: VehicleTelemetry) => void): void;
    addEventListener(event: 'schedule', cb: (schedule: Schedule) => void): void;
    addEventListener(event: 'vehicleMetadataUpdates', cb: (updates: VehicleMetadataUpdates) => void): void;
    addEventListener(event: 'reconnect', cb: () => void): void;
    addEventListener(event: 'error', cb: (reason: unknown) => void): void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addEventListener(event: keyof typeof this.listeners, cb: any) {
        this.listeners[event].add(cb);
    }

    removeEventListener(event: 'vehicleTelemetry', cb: (telemetryVehicleTelemetry: VehicleTelemetry) => void): void;
    removeEventListener(event: 'schedule', cb: (schedule: Schedule) => void): void;
    removeEventListener(event: 'vehicleMetadataUpdates', cb: (updates: VehicleMetadataUpdates) => void): void;
    removeEventListener(event: 'reconnect', cb: () => void): void;
    removeEventListener(event: 'error', cb: (reason: unknown) => void): void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    removeEventListener(event: keyof typeof this.listeners, cb: any) {
        this.listeners[event].delete(cb);
    }

    async subscribeVehicleTelemetry(vehicleId: Vehicle.Id): Promise<void> {
        const req: VehicleTelemetryRequest = {
            token: 'dummy',
            channel: 'vehicleTelemetry',
            operation: 'subscribe',
            resource_id: vehicleId,
        };

        await this._send(req);
    }

    async unsubscribeVehicleTelemetry(vehicleId: Vehicle.Id) {
        const req: VehicleTelemetryRequest = {
            token: 'dummy',
            channel: 'vehicleTelemetry',
            operation: 'unsubscribe',
            resource_id: vehicleId,
        };

        await this._send(req);
    }

    private async waitForReconnect() {
        if (!this.ws) return;

        this.reconnecting = true;

        await this.ws.get();

        // Although the websocket might be disconnected and then be recreated automatically,
        // the fresh connection has no subscription.
        // Here, we notify `reconnect` event to give a chance to restore the subscriptions.
        if (this.reconnecting) {
            this.listeners.reconnect.forEach(cb => cb());
            this.reconnecting = false;
        }
    }

    private async _send(req: FMSWebsocketRequest) {
        if (!this.ws) {
            throw new SequenceError('not yet connected');
        }

        // We are going to wait until a valid connection is ready.
        // That means, unsent requests are queued on the runtime as async tasks.
        // If we need to control the queued requests, we should employ a pending array by ourselves.
        const ws = await this.ws.get();
        const { token } = await this.tokenKeeper.get();

        // Ok, every things are fine now. Go on.
        this.__send(ws, token, req);
    }

    private __send(ws: WebSocket, token: string, req: FMSWebsocketRequest) {
        ws.send(JSON.stringify(('token' in req) ? { ...req, token } : req));
    }

    private parseMessage(msg: MessageEvent) {
        try {
            return JSON.parse(msg.data) as Record<string, unknown>;
        } catch (e) {
            return undefined;
        }
    }

    private onVehicleTelemetry(vehicleTelemetry: VehicleTelemetry) {
        this.listeners.vehicleTelemetry.forEach(cb => cb(vehicleTelemetry));
    }

    private onSchedule(schedule: Schedule) {
        this.listeners.schedule.forEach(cb => cb(schedule));
    }

    private onVehicleMetadataUpdates(vehicleMetadataUpdates: VehicleMetadataUpdates) {
        this.listeners.vehicleMetadataUpdates.forEach(cb => cb(vehicleMetadataUpdates));
    }

    private onUnknownMessage(json: Record<string, unknown>) {
        const reason = new UnknownMessageError(json);
        this.listeners.error.forEach(cb => cb(reason));
    }

    private onUnsupportedMessage(event: MessageEvent) {
        const reason = new UnsupportedMessageError(event);
        this.listeners.error.forEach(cb => cb(reason));
    }
}
